import React, { useState } from 'react';
import { X } from 'lucide-react';

interface ConfirmedTravelFormProps {
  initialTravel?: {
    id: string;
    flightNumber: string;
    airline: string;
    departureAirport: string;
    arrivalAirport: string;
    departureTime: Date;
    arrivalTime: Date;
  };
  onSubmit: (data: {
    flightNumber: string;
    airline: string;
    departureAirport: string;
    arrivalAirport: string;
    departureTime: Date;
    arrivalTime: Date;
  }) => void;
  onClose: () => void;
}

export function ConfirmedTravelForm({ initialTravel, onSubmit, onClose }: ConfirmedTravelFormProps) {
  const [error, setError] = useState<string | null>(null);

  const formatDateTimeLocal = (date: Date) => {
    return date.toISOString().slice(0, 16);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const departureDateTime = formData.get('departureDateTime') as string;
    const arrivalDateTime = formData.get('arrivalDateTime') as string;

    if (!departureDateTime || !arrivalDateTime) {
      setError('Please select both departure and arrival times');
      return;
    }

    const departureTime = new Date(departureDateTime);
    const arrivalTime = new Date(arrivalDateTime);

    if (arrivalTime <= departureTime) {
      setError('Arrival time must be after departure time');
      return;
    }

    onSubmit({
      flightNumber: formData.get('flightNumber') as string,
      airline: formData.get('airline') as string,
      departureAirport: formData.get('departureAirport') as string,
      arrivalAirport: formData.get('arrivalAirport') as string,
      departureTime,
      arrivalTime
    });
  };

  return (
    <div className="modal-container">
      <div className="modal-content">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold">
            {initialTravel ? 'Edit Confirmed Travel' : 'Add Confirmed Travel'}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-black/20 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Flight Number</label>
              <input
                type="text"
                name="flightNumber"
                defaultValue={initialTravel?.flightNumber}
                placeholder="e.g., AA123"
                className="w-full px-4 py-2 bg-black/20 rounded-xl text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Airline</label>
              <input
                type="text"
                name="airline"
                defaultValue={initialTravel?.airline}
                placeholder="e.g., American Airlines"
                className="w-full px-4 py-2 bg-black/20 rounded-xl text-white"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Departure Airport</label>
              <input
                type="text"
                name="departureAirport"
                defaultValue={initialTravel?.departureAirport}
                placeholder="e.g., LAX - Los Angeles"
                className="w-full px-4 py-2 bg-black/20 rounded-xl text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Arrival Airport</label>
              <input
                type="text"
                name="arrivalAirport"
                defaultValue={initialTravel?.arrivalAirport}
                placeholder="e.g., JFK - New York"
                className="w-full px-4 py-2 bg-black/20 rounded-xl text-white"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Departure Date & Time</label>
              <input
                type="datetime-local"
                name="departureDateTime"
                defaultValue={initialTravel ? formatDateTimeLocal(initialTravel.departureTime) : undefined}
                className="w-full px-4 py-2 bg-black/20 rounded-xl text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Arrival Date & Time</label>
              <input
                type="datetime-local"
                name="arrivalDateTime"
                defaultValue={initialTravel ? formatDateTimeLocal(initialTravel.arrivalTime) : undefined}
                className="w-full px-4 py-2 bg-black/20 rounded-xl text-white"
                required
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-500/10 text-red-500 p-3 rounded-xl mb-4 text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-[#bbdde5] text-black rounded-xl py-3 font-medium hover:opacity-90 transition-opacity"
          >
            {initialTravel ? 'Update Travel Details' : 'Add Travel Details'}
          </button>
        </form>
      </div>
    </div>
  );
}